/**
 * Type-aware autocomplete voor het Onderwater venster (M2 §2.1).
 *
 * Triggert binnen 't PlantUML-paneel: tijdens 't typen van een
 * concept-procedure (`App|` → `Application_Component`) of een
 * relatie-procedure (`Rel_S|` → `Rel_Specialization`, `Rel_Serving`)
 * toont CodeMirror een dropdown uit de bekende emitter-tokens.
 *
 * Alle drie sub-features zijn live (M8 §6):
 *  1. procedure-namen (Ctrl+Space) + concept-namen (auto binnen Rel_-quotes)
 *  2. naam→id: completion plakt `"naam"`, `substituteNames` zet 'm op de
 *     volgende parse om naar `n_<uuid>`
 *  3. hover-tooltip op `n_<uuid>` toont naam + type + «stereotype» + beschrijving
 */

import { RELATION_PROCEDURE, TYPE_PROCEDURE } from "@ae/domain";
import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import type { Extension } from "@codemirror/state";
import { hoverTooltip } from "@codemirror/view";

import { getRegistry, resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";

/**
 * Bouwt de statische completion-set vanuit de emitter-tokens. Elke
 * procedure verschijnt één keer in de lijst, met een korte detail-
 * regel die teruggeeft welk domain-typeId 'm produceert — zo kan de
 * modelleur snel zien welk concept de procedure inserteert.
 */
function buildStaticCompletions(): Completion[] {
  const out: Completion[] = [];
  for (const [typeId, procedure] of Object.entries(TYPE_PROCEDURE)) {
    out.push({
      label: procedure,
      type: "function",
      detail: typeId,
      // Voeg `(` toe na de procedure-naam zodat de modelleur direct in
      // de argument-positie staat na completion.
      apply: `${procedure}(`,
    });
  }
  for (const [typeId, procedure] of Object.entries(RELATION_PROCEDURE)) {
    out.push({
      label: procedure,
      type: "function",
      detail: typeId,
      apply: `${procedure}(`,
    });
  }
  // Dedupe op label (een paar typeIds delen dezelfde procedure-naam
  // via legacy-aliases; behoud de eerst-gevonden detail-regel).
  const seen = new Set<string>();
  return out.filter((c) => {
    if (seen.has(c.label)) return false;
    seen.add(c.label);
    return true;
  });
}

const STATIC_COMPLETIONS = buildStaticCompletions();

/**
 * Completion-source voor PROCEDURE-namen. Blijft expliciet (Ctrl+Space)
 * — auto-fire zou tijdens normaal typen aanslaan en Enter/Tab/Arrow als
 * accept-keys binden, met "rara"-effecten als gevolg.
 */
function procedureCompletions(context: CompletionContext): CompletionResult | null {
  // Alleen reageren wanneer de modelleur 'm bewust opent. Niet-explicit
  // verzoeken laten we doorvallen naar de concept-name-source (die wel
  // auto-firet binnen quotes).
  if (!context.explicit) return null;
  const word = context.matchBefore(/[A-Za-z_][A-Za-z0-9_]*/);
  if (!word) {
    return {
      from: context.pos,
      options: STATIC_COMPLETIONS,
      validFor: /^[A-Za-z_][A-Za-z0-9_]*$/,
    };
  }
  return {
    from: word.from,
    options: STATIC_COMPLETIONS,
    validFor: /^[A-Za-z_][A-Za-z0-9_]*$/,
  };
}

/**
 * Bepaalt of de cursor in een quoted string staat in arg-positie 0 of 1
 * van een `Rel_<Name>(…)`-call. Returnt `null` als cursor niet in zo'n
 * positie staat, anders `{ quoteStart, prefix }` met de absolute positie
 * van de openende `"` en de tekst tussen `"` en cursor.
 *
 * We werken puur regel-lokaal — Rel-calls beslaan altijd één regel in onze
 * emitter-output, en zelfs handgetypte multi-line calls zouden te
 * complex zijn om in een dropdown-trigger correct te tracken.
 */
function detectRelArgQuoteContext(
  lineText: string,
  cursorInLine: number,
): { quoteStart: number; prefix: string } | null {
  // Eerste check: matcht 't begin van de regel een `Rel_<Word>(`-opening?
  // We zoeken naar `(` na de proc-naam en dwingen 'm te bestaan vóór de
  // cursor — anders is de modelleur nog bezig met de proc-naam zelf en
  // valt 't onder de procedure-completions, niet hier.
  const relMatch = /^\s*(Rel_[A-Za-z][\w]*)\s*\(/.exec(lineText);
  if (!relMatch) return null;
  const argsStart = relMatch[0].length;
  if (cursorInLine < argsStart) return null;

  // Walk through args from argsStart to cursorInLine, tracking quote
  // state + arg index. We willen weten: staan we binnen een quoted
  // string, en zo ja in welke arg-positie?
  let i = argsStart;
  let argIndex = 0;
  let inQuote = false;
  let quoteStart = -1;
  let escaped = false;
  while (i < cursorInLine) {
    const ch = lineText[i] as string;
    if (inQuote) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inQuote = false;
        quoteStart = -1;
      }
      // Komma's binnen quotes tellen niet als arg-separator.
    } else {
      if (ch === '"') {
        inQuote = true;
        quoteStart = i;
      } else if (ch === ",") {
        argIndex++;
      } else if (ch === ")") {
        // Sluit-paren buiten quotes → call is afgelopen vóór cursor;
        // cursor staat dus niet meer in een arg-positie.
        return null;
      }
    }
    i++;
  }
  if (!inQuote || quoteStart < 0) return null;
  if (argIndex > 1) return null; // alleen arg 0 + 1 (source/target)
  const prefix = lineText.slice(quoteStart + 1, cursorInLine);
  // De prefix mag geen `"` of newline bevatten (escape-state hebben we
  // hierboven al getrackt; als we tot hier komen is de string nog open).
  return { quoteStart, prefix };
}

/**
 * Bouwt de concept-name-completions on-demand uit de live workspace-
 * store. Per concept één entry; label is de NL- of EN-naam afhankelijk
 * van de UI-lang. Detail-regel toont 't andere taalveld (zodat de
 * modelleur de cross-lang aliases ook ziet) of een short-id fallback.
 *
 * De `apply`-string wordt door CodeMirror op de `from..to`-range
 * geplakt; caller zet die range van openende `"` t/m huidige cursor —
 * onze apply-string `"<naam>"` overschrijft dus de halve geopende
 * quote-string door 'n compleet quoted fragment. SubstituteNames doet
 * vervolgens op de volgende parse-debounce de naam→`n_<uuid>` vertaling.
 */
function buildConceptCompletions(lang: "nl" | "en", prefix: string): Completion[] {
  const store = useWorkspaceData.getState().store;
  const lower = prefix.toLowerCase();
  const out: Completion[] = [];
  const altLang: "nl" | "en" = lang === "nl" ? "en" : "nl";
  for (const c of store.listConcepts()) {
    if (c.tombstoneInBranch) continue;
    const primary = resolveBilingual(c.name, lang).trim();
    if (!primary) continue;
    // Filter: alleen items waarvan EEN VAN DE TAALVELDEN met de prefix
    // begint (case-insensitive). Lege prefix → toon alles (max 50).
    const alt = (c.name[altLang] ?? "").trim();
    const matchesPrimary = lower === "" || primary.toLowerCase().startsWith(lower);
    const matchesAlt = lower !== "" && alt.toLowerCase().startsWith(lower);
    if (!matchesPrimary && !matchesAlt) continue;
    out.push({
      label: primary,
      type: "variable",
      detail: alt && alt !== primary ? alt : c.id.slice(0, 8),
      apply: `"${primary}"`,
    });
    if (out.length >= 50) break;
  }
  return out;
}

/**
 * Completion-source voor CONCEPT-NAMEN. Auto-firet zodra de cursor in
 * een quoted string in arg 0 of 1 van een `Rel_*`-call staat. Buiten
 * die context returnt 'ie null en geeft 't stokje door aan de procedure-
 * source.
 */
function makeConceptNameCompletions(
  lang: "nl" | "en",
): (context: CompletionContext) => CompletionResult | null {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const cursorInLine = context.pos - line.from;
    const ctx = detectRelArgQuoteContext(line.text, cursorInLine);
    if (!ctx) return null;
    const options = buildConceptCompletions(lang, ctx.prefix);
    if (options.length === 0) return null;
    // `from` = absolute positie van openende `"`. Vervangen tot
    // cursor-positie, omdat we de `"prefix"` weghalen en `"naam"`
    // ervoor inserten.
    const from = line.from + ctx.quoteStart;
    return {
      from,
      to: context.pos,
      options,
      // Filtering moet door CodeMirror gebeuren maar onze prefix-match
      // is al strict — laat validFor 'n re-query triggeren zodra de
      // modelleur buiten 't huidige quoted-fragment uitkomt.
      validFor: /^"[^"\n]*$/,
    };
  };
}

/**
 * Bouwt de autocompletion-extension. Roep dit aan vanuit de PlantUML-
 * editor-state — Mermaid-mode gebruikt andere syntax en valt buiten
 * scope van iteratie 1.
 *
 * @param lang — UI-taal voor concept-name-resolution.
 */
export function plantumlAutocomplete(lang: "nl" | "en" = "nl"): Extension {
  return autocompletion({
    override: [makeConceptNameCompletions(lang), procedureCompletions],
    // Auto-fire AAN, maar elk completion-source bewaakt z'n eigen
    // context: procedureCompletions returnt null tenzij `context.explicit`
    // (Ctrl+Space), conceptNameCompletions firet alleen binnen quoted
    // Rel_*-args. Buiten beide contexts blijft de dropdown stil — geen
    // popup-spam tijdens normaal typen van proc-namen of comments.
    activateOnTyping: true,
    maxRenderedOptions: 50,
  });
}

/**
 * Hover-tooltip op concept-aliases (sub-feature 3 uit 03-mvp-scope §2.1).
 *
 * De PlantUML-emitter produceert `n_<uuid-met-underscores>` als alias
 * voor elk concept. Wanneer de modelleur over zo'n alias hovert in 't
 * Onderwater venster verschijnt 'n tekstballon met de naam (NL/EN
 * resolved via lang-prop) van 't onderliggende concept. Onbekende
 * aliases → geen tooltip (silent — geen confusing-no-match-popup).
 *
 * @param lang — UI-taal voor naam-resolutie; "nl" of "en".
 */
export function plantumlAliasHoverTooltip(lang: "nl" | "en"): Extension {
  return hoverTooltip((view, pos) => {
    // Word-grens: n_<hex/underscores>. We accepteren elke
    // `n_[A-Za-z0-9_]+` zodat zowel UUID-vorm (n_abc_123_...) als
    // simpele namen (n_winkel) ge-tooltipt worden.
    const line = view.state.doc.lineAt(pos);
    const lineText = line.text;
    const offset = pos - line.from;
    // Scan vooruit + achteruit naar word-grenzen.
    const wordStart = (() => {
      let i = offset;
      while (i > 0 && /[A-Za-z0-9_]/.test(lineText[i - 1] ?? "")) i--;
      return i;
    })();
    const wordEnd = (() => {
      let i = offset;
      while (i < lineText.length && /[A-Za-z0-9_]/.test(lineText[i] ?? "")) i++;
      return i;
    })();
    if (wordStart === wordEnd) return null;
    const word = lineText.slice(wordStart, wordEnd);
    if (!word.startsWith("n_")) return null;
    // Vertaal alias-vorm terug naar UUID (underscores → hyphens).
    const conceptId = word.slice(2).replace(/_/g, "-");
    const concept = useWorkspaceData.getState().store.getConcept(conceptId);
    if (!concept) return null;
    const displayName = resolveBilingual(concept.name, lang) || concept.id;
    // Type-label + actieve stereotypes + beschrijving (M8 §6-c): de tooltip
    // toont naam, type en — indien aanwezig — de beschrijving.
    const reg = getRegistry();
    const et = reg.getElementType(concept.typeId);
    const typeLabel = et ? resolveBilingual(et.label, lang) : concept.typeId;
    const stereo = concept.stereotypes
      .map((sid) => {
        const st = reg.getStereotype(sid);
        return st ? `«${resolveBilingual(st.label, lang)}»` : "";
      })
      .filter(Boolean)
      .join(" ");
    const description = resolveBilingual(concept.description, lang);
    return {
      pos: line.from + wordStart,
      end: line.from + wordEnd,
      above: true,
      create() {
        const dom = document.createElement("div");
        dom.className =
          "flex max-w-xs flex-col gap-0.5 rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 shadow";
        const nameEl = document.createElement("div");
        nameEl.textContent = displayName;
        nameEl.className = "font-semibold text-(--ld-text) text-xs";
        dom.appendChild(nameEl);
        const typeEl = document.createElement("div");
        typeEl.textContent = stereo ? `${typeLabel} ${stereo}` : typeLabel;
        typeEl.className = "text-xs text-(--ld-muted)";
        dom.appendChild(typeEl);
        if (description && description !== "(unnamed)") {
          const descEl = document.createElement("div");
          descEl.textContent = description;
          descEl.className = "text-xs text-(--ld-muted)";
          dom.appendChild(descEl);
        }
        // Niet-bewerkbaar; CodeMirror plaatst 'm zelf.
        return { dom };
      },
    };
  });
}
